import './Awarness.scss';

export const AwarnessTermsExplainations = () => {
  return (
    <div className="awarnessContainer row justify-content-center px-1">
      <div className="col-12 col-md-8">
        <h1>Discrimination &amp; Sensitivity: Explanations</h1>
        <h4>
          These explanations are not exhaustive or complete; they are merely
          intended to provide guidance on certain terms and topics that are
          important to us.
        </h4>
        <br />
        <h2>
          <a href="#consent" id="consent">
            #
          </a>{' '}
          Consent{' '}
        </h2>
        <p>Only ‘yes’ means ‘yes’!</p>
        <p>
          'Maybe', ‘I don't know’, ‘not really’ and the like are not active
          consent. 'No' means no. Asking for or actively expressing consent can
          be very unfamiliar. We are often wrong when we unreflectingly or
          tacitly assume what someone (does not) want. Therefore: Consensus is
          the active agreement of all those involved.
        </p>
        <h2>
          <a href="#self-designation" id="self-designation">
            #
          </a>{' '}
          Self-designation
        </h2>
        <p>
          People are often referred to by categories or designations to which
          they do not feel they belong or which they themselves would describe
          differently (external designation). Self-designations, on the other
          hand, are names that a socially marginalized group chooses for itself.
          An example of this is BIPoC{' '}
          <a href="#bipoc" className="see-more-on">
            (see paragraph on BIPoC)
          </a>
          .
        </p>
        <p>
          Which pronoun does a person want to be addressed with? If you are
          unsure about how a person or group would like to be addressed or
          referred to, it is a good idea to ask. An expression such as “The
          person is read by me as ...” leaves the actual affiliation to a group
          open.
        </p>
        <h2>
          <a href="#discrimination" id="discrimination">
            #
          </a>{' '}
          Discrimination
        </h2>
        <p>
          Discrimination in the legal sense is any unjustified unequal treatment
          based on “race”, ethnic origin, gender, religion, world view,
          disability, age or sexual orientation. Discrimination can occur
          consciously or unconsciously. It is linked to prejudices or
          stereotypical expectations of normality, for example. It can happen
          openly and directly (direct discrimination), for example when someone
          is not invited to a job interview because of their name, or is
          rejected when looking for accommodation because of their skin color.
        </p>
        <p>
          Discrimination is a social phenomenon of placing at a disadvantage,
          segregation and devaluation. Groups or individuals are disadvantaged,
          devalued and oppressed on the basis of (supposed) characteristics,
          e.g. ethnic or social origin, religion or gender (so-called
          multidimensional discrimination).
        </p>
        <h2>
          <a href="#privileges" id="privileges">
            #
          </a>{' '}
          Privileges
        </h2>
        <p>
          Privileges are often referred to as social advantages that one has due
          to a supposed membership of a socially constructed group. Privileges
          open up opportunities and scope for action. People who are ascribed a
          social ‘normality’ (e.g. white, male, cisgender, able bodied, etc.)
          are considered privileged. Positions that deviate from this, i.e. the
          supposed ‘others’, are disadvantaged, i.e. discriminated against.
        </p>
        <h2>
          <a href="#ageism" id="ageism">
            #
          </a>{' '}
          Age discrimination, ageism, adultism
        </h2>
        <p>
          If children, young people or older people are treated unequally solely
          because of their age and without a valid reason, this is age
          discrimination. For example, if people of an older age are suddenly no
          longer taken seriously and their view of the world is no longer
          considered relevant, simply because of their age, this is a form of
          discrimination. Unfortunately, we find it rather “normal” in our
          society when children and young people are discriminated against.
        </p>
        <p>
          Discrimination against children and young people is also known as
          adultism or epiphanism.
        </p>
        <p>
          Discrimination against older people is often referred to as ageism
          (from age = old age).
        </p>
        <h2>
          <a href="#ableism" id="ableism">
            #
          </a>{' '}
          Ableism
        </h2>
        <p>
          “Ableism denotes the revaluation and devaluation of people according
          to the abilities attributed to them, which leads to discrimination
          against chronically ill and disabled people.
        </p>
        <p>
          Ableism assumes a physical and psychological ideal standard of the
          human being, which chronically ill and/or disabled people cannot live
          up to. They are therefore considered ‘inferior’.
        </p>
        <p>
          On a social level, it means that disabled and/or chronically ill
          people are often excluded and made invisible." (loosely translated
          from{' '}
          <a
            target="_blank"
            className="see-more-on"
            href="https://leidmedien.de/begriffe-ueber-behinderung-von-a-bis-z/"
            rel="noreferrer"
          >
            leidmedien.de/begriffe
          </a>
          )
        </p>
        <h2>
          <a href="#lookism" id="lookism">
            #
          </a>{' '}
          Lookism
        </h2>
        <p>
          is discrimination on the basis of appearance and supposed ‘positive’
          and ‘negative’ external characteristics, e.g. in relation to clothing,
          body shape and style. It is based on an assumed standardized ideal of
          beauty and the assumption that a person's value or skills are related
          to this.
        </p>
        <h2>
          <a href="#fatphobia" id="fatphobia">
            #
          </a>{' '}
          Fatphobia
        </h2>
        <p>
          Fatphobia is a form of prejudice based on body size, where negative
          assumptions and judgments are made about individuals based on their
          weight. It perpetuates the belief that thinner bodies are superior,
          healthier, or more attractive, often disregarding the diversity of
          natural body shapes and sizes.
        </p>
        <p>
          Fatphobia refers to the irrational fear, discrimination, or aversion
          toward individuals who are perceived as overweight or obese. Rooted in
          societal biases, fatphobia manifests in prejudice, stereotypes, and
          systemic inequities that marginalise individuals based on their body
          size. This bias permeates various facets of life, including media
          representation, workplace dynamics, healthcare access, and personal
          relationships.
        </p>
        <h2>
          <a href="#diversity" id="diversity">
            #
          </a>{' '}
          Diversity
        </h2>
        <p>
          It means variety. People and bodies are diverse. In this context, this
          refers to diversity in terms of gender, sexual orientation,
          geographical, cultural, social origin, skin color, religion, age, etc.
        </p>
        <h2>
          <a href="#classism" id="classism">
            #
          </a>{' '}
          Classism
        </h2>
        <p>
          It refers to discrimination based on social origin and/or social and
          economic position. Classism is therefore not just about how much money
          someone has at their disposal, but also about their status and the
          financial and social circumstances in which they grew up.
        </p>
        <p>
          Classism is mainly directed against people of a “lower class”. In
          particular, homeless and unemployed people and people from the working
          and poverty classes are marginalized.
        </p>
        <h2>
          <a href="#social-chauvinism" id="social-chauvinism">
            #
          </a>{' '}
          Social chauvinism
        </h2>
        <p>
          It means the discrimination of people on the basis of their social
          origin or ascribed social position in society. Social chauvinism
          mainly affects people who are unemployed and affected by poverty,
          homeless people, laborers or people with no or low educational
          qualifications.
        </p>
        <h2>
          <a href="#bipoc" id="bipoc">
            #
          </a>{' '}
          BIPoC
        </h2>
        <p>
          stands for Black, Indigenous, People/Person of Color and is a
          self-designation of people who experience racism because they are not
          perceived as white or of the citizenship of the country they are in.
        </p>
        <h2>
          <a href="#racism" id="racism">
            #
          </a>{' '}
          Racism
        </h2>
        <p>
          is the discrimination and devaluation of people on the basis of
          supposed origin, skin color, culture and religion. The word ‘racism’
          is derived from the term ‘race’, which (although still used in the
          German Grundgesetz / constitution) is anthropologically indefensible.
          The division of people into alleged ‘races’ originated at the time of
          colonization. At that time, attempts were made to scientifically
          justify the exploitation and enslavement of people in Africa, South
          America and Asia with the alleged superiority of the ‘white race’.
        </p>
        <p>
          The “racial theory” and the classification of people according to
          biological criteria have long been refuted by researchers and were
          entirely fictional. However, the ideas that emerged back then are
          still being spread and believed.
        </p>
        <p>
          People continue to be differentiated according to physical
          characteristics such as skin color, eye, nose and mouth shapes, hair
          structure or characteristics that people wear on or around their
          bodies, such as a hijab, and condemned as ‘inferior’, ‘foreign’ or
          ‘abnormal’.
        </p>
        <p>
          There are different forms of racism: anti-Asian racism, islamophobia,
          antiziganism, antisemitism, ethnopluralism, anti-black racism. A
          subtype is cultural racism, which is always present when habits,
          customs and traditions are seen as negative and inferior to one's own
          culture ({' '}
          <a
            className="see-more-on"
            target="_blank"
            href="https://www.damigra.de/wp-content/uploads/DaMigra_Speak-up-Sister_Dokumentation-des-Projektes-Gleichberechtigte-Politische-Teilhabe.pdf"
            rel="noreferrer"
          >
            see also DaMigra, 2019, Speak Up Sister!
          </a>{' '}
          ).
        </p>
        <h3>
          <a href="#islamophobia" id="islamophobia">
            #
          </a>{' '}
          » Islamophobia / Anti-Muslim racism
        </h3>
        <p>
          Discrimination occurs because people are seen as belonging to the
          religion - regardless of whether they are Muslim. The devaluation is
          based on the idea of Islam as a non-white and non-European religion,
          which is presented as incompatible with the “Western values” of the
          so-called “Christian-Jewish Occident”.
        </p>
        <h3>
          <a href="#antisemitism" id="antisemitism">
            #
          </a>{' '}
          » Antisemitism
        </h3>
        <p>
          It appears in different forms and has adapted to the respective
          contexts over the centuries. Anti-Semitism is not about the
          individual, but about the principle of ‘Jew’, which is associated with
          conspiracy theories and inhuman attributions. In contrast to other
          forms of discrimination, the supposed axis of power runs from the
          bottom up: A small powerful group is invented (constructed) to be
          responsible for the misfortunes of a large community. Antisemitism
          includes, for example, directly expressed hatred of Jews, conspiracy
          thinking, denial and relativization of the Shoah and others.
        </p>

        <h3>
          <a href="#everyday-racism" id="everyday-racism">
            #
          </a>{' '}
          » Everyday racism
        </h3>
        <p>
          We speak of everyday racism when we are not dealing with ideologically
          based and entrenched racism, but with racist incidents in the
          so-called ”middle of society“, which must be dealt with in the same
          way. These often happen in the form of (but are not limited to)
          microaggressions, like a white person “innocently” asking a person of
          different skin culture what country they are from.
        </p>
        <h3>
          <a href="#anti-black-racism" id="anti-black-racism">
            #
          </a>{' '}
          » Anti-Black racism
        </h3>
        <p>
          Racism against Black people is also referred to as colonial racism.
          The colonial states used anti-Black racism to justify the genocides,
          enslavement, rape, exploitation and oppression of people in the
          occupied territories: Not only was it falsely propagated that there
          were biological “human races”, but value judgments were also
          introduced: white people were seen as superior and of higher value,
          black people as inferior and inferior. It was spread that at the very
          least, white and black people were fundamentally different (othering).
          The consequences of this thinking still lead to anti-Black racism
          today.
        </p>
        <h2>
          <a href="#patriarchy" id="patriarchy">
            #
          </a>{' '}
          Patriarchy
        </h2>
        <p>
          This literally means “rule of the father”. The word is used to
          describe a society in which masculinity is valorized and femininity is
          devalued. It means that men shape, control and represent society.
        </p>
        <h2>
          <a href="#sexism" id="sexism">
            #
          </a>{' '}
          Sexism
        </h2>
        <p>
          This term refers to devaluation based on biological and/or chosen
          gender identity. Women and people not conforming to the male gender
          “norm” are affected. Sexism is a form of patriarchal oppression.
        </p>
        <h2>
          <a href="#heteronormativity" id="heteronormativity">
            #
          </a>{' '}
          Heteronormativity
        </h2>
        <p>
          This is the assumption that “all people are either cis-women or
          cis-men and that only men and women can find each other sexually
          attractive or fall in love with each other.” Heteronormativity is also
          the assumption that women and men have a certain ‘nature’, ‘behavior’,
          ‘taste’ and ‘preference’ and behave in a certain way. In other words,
          it is a socially produced ‘norm’ that only designates heterosexuality
          as the standard. All other lifestyles, such as homo*sexuality, are
          therefore seen as deviant or exceptional or special, in many cases
          they are seen as wrong.
        </p>
        <h2>
          <a href="#homophobia" id="homophobia">
            #
          </a>{' '}
          Homophobia
        </h2>
        <p>
          It describes the discrimination of queer people. It is based on
          ‘heteronormativity’, i.e. the (unconscious) expectation that all
          people are cisgender and heterosexual. Other gender identities and
          forms of desire are devalued, marginalized or made invisible.
          Sometimes written as homo*phobia, the * stands for people who do not
          fall into the categories of ‘man’ or ‘woman’.
        </p>
        <h2>
          <a href="#lgbtqiaplus" id="lgbtqiaplus">
            #
          </a>{' '}
          LGBTQIA+
        </h2>
        <p>
          It stands for Lesbian, Gay, Bisexual, Trans*, Queer, Intersex, Asexual
          and others. The plus denoting that many others belong, and the term is
          inclusive.
        </p>
        <h2>
          <a href="#intersex" id="intersex">
            #
          </a>{' '}
          Intersex / intersex person
        </h2>
        <p>
          This refers to the congenital presence of genetic and/or anatomical
          and/or hormonal sexual characteristics that do not correspond to the
          gender norms of “man” and “woman”. In many places, heteronormativity
          is still so prevalent that corrective surgery is performed on intersex
          children, with some of them only learning later in life that they even
          are intersex. So in contrast to other mentioned groups who are exposed
          to discrimination, intersex people also experience a severe form of
          erasure.
        </p>
        <h2>
          <a href="#cisgender" id="cisgender">
            #{' '}
          </a>{' '}
          Cis, cisgender
        </h2>
        <p>
          Cis is a term referring to a person who identifies with the gender
          assigned to them at birth. The term can be problematic for intersex
          people because the juxtaposition in the usage of “cis” and “trans*”
          can render them invisible, or incorrectly assign them to “trans*”. It
          should therefore be used with caution.
        </p>
        <h2>
          <a href="#transgender" id="transgender">
            {' '}
            #
          </a>{' '}
          Trans*
        </h2>
        <p>
          Transgender is an umbrella term encompassing all gender-identities
          that lie outside of the cis “norm”. The asterisk was added to
          specifically denote that it is an inclusive term.
        </p>
        <h2>
          <a href="#transphobia" id="transphobia">
            #
          </a>{' '}
          Transphobia
        </h2>
        <p>
          It describes discrimination against people whose sex assigned at birth
          does not match their gender identity, or those where the
          discriminating party incorrectly assumes such.
        </p>
      </div>
    </div>
  );
};
